import React, { useState } from "react";
import ModalContainer from "../ModalContainer";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import {
  getAllBookings,
  getBookingOtp,
  verifyBooking,
} from "../../../redux/action/bookingActions";
import { Alert, Table } from "react-bootstrap";
import { BOOKINGSTATUS, DATETIMEFORMAT, TIMEFORMAT, TIMEONLYFORMAT } from "../../../contants";
import useTodaysBookings from "../../../hooks/useTodaysBookings";

/**
 * Determines the time-based status of a reservation.
 * @param {Date|string} start - Reservation start time
 * @param {Date|string} end   - Reservation end time
 * @param {Date} now          - Current time (injected for testability)
 * @returns {"ongoing"|"upcoming"|"completed"}
 */
export const getReservationStatus = (start, end, now = new Date()) => {
  const startMs = new Date(start).getTime();
  const endMs = new Date(end).getTime();
  const nowMs = now.getTime();

  if (nowMs >= startMs && nowMs < endMs) return "ongoing";
  if (nowMs < startMs) return "upcoming";
  return "completed";
};

const RESERVATION_STATUS_LABEL = {
  ongoing: "🟢 Ongoing",
  upcoming: "🔵 Upcoming",
  completed: "🔴 Completed",
};

const STATUS_THEME = {
  ongoing: {
    color: "success",
    gradient: "linear-gradient(to right, #e8f5e9, #ffffff)",
  },
  upcoming: {
    color: "primary",
    gradient: "linear-gradient(to right, #e3f2fd, #ffffff)",
  },
  completed: {
    color: "danger",
    gradient: "linear-gradient(to right, #ffebee, #ffffff)",
  },
};

const TodaysReservationModal = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const { branchId, restaurantId } = useSelector((state) => state.user);
  const bookings = useTodaysBookings();

  const [otps, setOtps] = React.useState({});
  const [expandedId, setExpandedId] = useState(null);

  // Tick every minute so row colors update automatically
  const [currentTime, setCurrentTime] = React.useState(new Date());
  React.useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60 * 1000);
    return () => clearInterval(timer);
  }, []);

  const handleChange = (e, bId) => {
    const {
      target: { value },
    } = e;

    setOtps({
      ...otps,
      [bId]: value,
    });
  };

  const handleSendOtp = (b) => {
    dispatch(
      getBookingOtp({
        mobile: b.contactNumber,
        bookingId: b.id || b._id,
      })
    );
  };

  const handleVerify = (b) => {
    dispatch(
      verifyBooking(
        {
          otp: otps[b.id || b._id],
          bookingId: b.id || b._id,
        },

        () => {
          setOtps({
            ...otps,
            [b.id || b._id]: "",
          });
          dispatch(
            getAllBookings({
              branchId,
              start: moment().startOf("day").toDate(),
              end: moment().endOf("day").toDate(),
            })
          );
        }
      )
    );
  };

  return (
    <ModalContainer
      open={open}
      onClose={() => {
        onClose();
        // setFormErrors();
        // reset();
      }}
      title={`Today's Reservations`}
      size="lg"
    >
      {bookings.length === 0 && (
        <Alert variant={"info"}>No bookings found today</Alert>
      )}

      {bookings.length > 0 && (
        <>
          {/* Reservation List (Card View for both Desktop & Mobile) */}
          <div className="reservation-list" style={{ padding: "5px" }}>
            {[...bookings]
              .sort((a, b) => new Date(a.start) - new Date(b.start))
              .map((b, i) => {
                const bId = b.id || b._id;
                const isExpanded = expandedId === bId;

                const reservationStatus = getReservationStatus(b.start, b.end, currentTime);
                const theme = STATUS_THEME[reservationStatus];
                const bootstrapColor = theme.color;
                const cardGradient = theme.gradient;

                return (
                  <div
                    key={i}
                    className={`card shadow-sm mb-3 border-${bootstrapColor}`}
                    style={{
                      cursor: "pointer",
                      borderLeftWidth: "4px",
                      background: cardGradient
                    }}
                    onClick={() => setExpandedId(isExpanded ? null : bId)}
                  >
                    <div className="card-body p-3">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <h6 className="mb-1 font-weight-bold text-dark">
                            Table(s): {b?.tables?.map((t) => t.tableNumber).join(", ") || "N/A"}
                          </h6>
                          <div className="text-muted small">
                            <i className="mdi mdi-clock-outline mr-1"></i>
                            {moment(b.start).format('hh:mm A')} - {moment(b.end).format('hh:mm A')}
                          </div>
                        </div>
                        <div className="text-right">
                          <span className={`badge badge-soft-${bootstrapColor} px-2 py-1 fs-4`} style={{ fontSize: "15px" }}>
                            {RESERVATION_STATUS_LABEL[reservationStatus]}
                          </span>
                        </div>
                      </div>

                      {isExpanded && (
                        <div className="mt-3 pt-3 border-top">
                          <div className="row">
                            <div className="col-6 mb-2">
                              <small className="text-muted d-block mb-1">Hosted By</small>
                              <strong className="text-dark">{b.hostedBy || "N/A"}</strong>
                            </div>
                            <div className="col-6 mb-2">
                              <small className="text-muted d-block mb-1">Mobile</small>
                              <strong className="text-dark">{b.contactNumber || "N/A"}</strong>
                            </div>
                            <div className="col-6 mb-2">
                              <small className="text-muted d-block mb-1">Number of Guests</small>
                              <strong className="text-dark">{b.numberOfPersons || "N/A"}</strong>
                            </div>
                            {b.decorationIncluded && (
                              <div className="col-6 mb-2">
                                <small className="text-muted d-block mb-1">Decoration</small>
                                <strong className="text-dark">Included</strong>
                              </div>
                            )}
                            {b.menuDetails && (
                              <div className="col-12 mt-2">
                                <small className="text-muted d-block mb-1">Menu</small>
                                <span className="text-dark">{b.menuDetails}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
          </div>
        </>
      )}
    </ModalContainer>
  );
};

export default TodaysReservationModal;
